import { useTranslation } from 'react-i18next';
import { ReactNode } from 'react';

import { useLangStore } from '@app/dashboard/store/lang';
import { EMPTY_VALUE } from '@app/dashboard/constants/shared';
import { COUNTRIES } from '@app/dashboard/constants/countries';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import { convertAmountToFixedDigits, convertCentsToMajorUnit } from '@app/dashboard/utils/convertAmounts';
import { formatAmountWithCommas, FormatCurrencyForDisplay, getLocalizedCurrency } from '@unified-checkout/design-system';
import { SummaryBox } from '@app/dashboard/components/inner_layout/Cards/SummaryBox';
import { getFromLocalStorage } from '@app/dashboard/utils/manageLocalStorage';

import { calculateInstantRefundFee, isKnet } from '../../utils';

const Breakdown = ({ singleTransactionDetails }: { singleTransactionDetails: Record<string, any> }) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const country = getFromLocalStorage(LOCAL_STORAGE_KEYS.COUNTRY);

  const { break_down, caf_details, discount_details, source_data, currency, caf, is_trx_bank_installment } =
    singleTransactionDetails || {};

  const isKnetTrx = isKnet(source_data?.type);
  const isInstantRefund = !!source_data?.instant_refund;
  const isBankInstallment = !!is_trx_bank_installment;
  const hasCaf = !!Object.values(caf || {}).length;
  const hasDiscount = !!(caf?.discount_amount || discount_details?.[0]?.discount_amount_cents);

  const cafDetailsFees =
    caf_details && (is_trx_bank_installment || source_data?.instant_refund)
      ? convertCentsToMajorUnit(calculateInstantRefundFee(caf_details) || 0, currency)
      : 0;

  const formatCafValue = (amountInCents?: number): string | ReactNode => {
    if (typeof amountInCents !== 'number') return EMPTY_VALUE;

    const currency = caf_details?.convenience_fee_currency || singleTransactionDetails?.currency;
    const formattedCurrency = FormatCurrencyForDisplay(getLocalizedCurrency(lang, currency), true);

    return `${formattedCurrency} 
       ${formatAmountWithCommas({
         amount: convertCentsToMajorUnit(amountInCents, currency),
         currency: currency,
       })}`;
  };

  const withCurrency = (amount: number) => `${currency} ${convertAmountToFixedDigits(amount, currency)}`;

  const getKnetPairs = () => {
    if (!isKnetTrx) return [];

    const { original_amount, original_currency, target_currency, kwd_conversion_rate, kwd_equivalent_amount } =
      break_down?.other_info || {};

    return [
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.transactionBreakdown.orderAmount'),
        value: !!original_amount
          ? `${getLocalizedCurrency(lang, original_currency)} ${break_down.other_info.original_amount}`
          : EMPTY_VALUE,
      },
      {
        label: `
        ${t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.transactionBreakdown.conversionRate')} 
        ${
          original_currency && target_currency
            ? `(${getLocalizedCurrency(lang, break_down.other_info.original_currency)} 
                - 
                ${getLocalizedCurrency(lang, break_down.other_info.target_currency)})
              `
            : ''
        }`,
        value: `${kwd_conversion_rate}` || EMPTY_VALUE,
      },
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.transactionBreakdown.transactionAmount'),
        value:
          kwd_equivalent_amount != null
            ? `${getLocalizedCurrency(lang, target_currency)} ${break_down.other_info.kwd_equivalent_amount}`
            : EMPTY_VALUE,
      },
    ];
  };

  const getOrderAmountPair = () => {
    const showOrderAmount = isBankInstallment || isInstantRefund || hasCaf || hasDiscount;

    if (!showOrderAmount) return [];

    return [
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.transactionBreakdown.orderAmount'),
        value: break_down?.order_amount,
      },
    ];
  };

  const getDiscountPair = () => {
    if (!hasDiscount) return [];

    return [
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.transactionBreakdown.discountAmount'),
        value: formatCafValue(caf?.discount_amount || discount_details?.[0]?.discount_amount_cents),
      },
    ];
  };

  const getPurchaseFeePair = () => {
    if (!isBankInstallment) return [];

    return [
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.transactionBreakdown.purchaseFee'),
        value: break_down.purchase_fees || EMPTY_VALUE,
      },
    ];
  };

  const getConvenienceFeePair = () => {
    if (!isBankInstallment && !hasCaf) return [];

    const appliedCafFees = caf?.convenience_fee_amount || 0;
    const maxCafFeesShouldApply = caf?.convenience_fee_max_cap || 0;
    const cafFeesAmount =
      maxCafFeesShouldApply !== 0 && appliedCafFees > maxCafFeesShouldApply
        ? maxCafFeesShouldApply
        : appliedCafFees;

    return [
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.transactionBreakdown.convenienceFee'),
        value: isBankInstallment ? withCurrency(cafDetailsFees) : formatCafValue(cafFeesAmount),
      },
    ];
  };

  const getInstantRefundFeesPair = () => {
    const hasInstantRefundFee = isInstantRefund && Array.isArray(caf_details) && !!caf_details.length;

    if (!hasInstantRefundFee) return [];

    return [
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.transactionBreakdown.instantRefundFees'),
        value: withCurrency(cafDetailsFees) || EMPTY_VALUE,
      },
    ];
  };

  const getCafTransactionAmountPair = () => {
    const hasCafTransaction = !!(caf?.transaction_amount || caf_details?.[0]?.amount_cents_with_caf);

    if (!hasCafTransaction) return [];

    return [
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.transactionBreakdown.transactionAmount'),
        value: formatCafValue(caf?.transaction_amount || caf_details?.[0]?.amount_cents_with_caf),
      },
    ];
  };

  const getTotalAmountPair = () => {
    if (isInstantRefund || isKnetTrx) return [];

    return [
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.transactionBreakdown.totalAmount'),
        value: break_down.total_amount || EMPTY_VALUE,
      },
    ];
  };

  const getFeesPairs = () => {
    const isEgypt = country === COUNTRIES.EGY;

    if (isEgypt) {
      return [
        {
          label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.transactionBreakdown.totalFees'),
          value: break_down.fees || EMPTY_VALUE,
        },
      ];
    }

    return [
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.transactionBreakdown.fees'),
        value: break_down.fees || EMPTY_VALUE,
      },
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.transactionBreakdown.vat'),
        value: break_down.vat || EMPTY_VALUE,
      },
    ];
  };

  const getTipAmountPair = () => {
    
    return [
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.transactionBreakdown.tipAmount'),
        value: break_down.tip_amount || EMPTY_VALUE,
      },
    ];
  };

  return (
    <SummaryBox
      title={t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.transactionBreakdown.title')}
      itemsList={[
        ...getKnetPairs(),
        ...getOrderAmountPair(),
        ...getDiscountPair(),
        ...getPurchaseFeePair(),
        ...getConvenienceFeePair(),
        ...getInstantRefundFeesPair(),
        ...getCafTransactionAmountPair(),
        ...getTotalAmountPair(),
        {
          label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.transactionBreakdown.capturedAmount'),
          value: break_down.capture_amount || EMPTY_VALUE,
        },
        ...getFeesPairs(),
        {
          label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.transactionBreakdown.netAmount'),
          value: break_down.net_amount || EMPTY_VALUE,
        },
        ...getTipAmountPair(),
      ]}
    />
  );
};

export default Breakdown;
